<template>
  <div class="modal-drag-custom-drag" :class="$vuetify.breakpoint.mobile && 'mobile-popup'" :style="{ zIndex: indexModalIndex }">
		<modal
			name="custom_drag_modal"
			:style="{ zIndex: indexModalIndex }"
			hide-overlay
			draggable=".drag-header"
			:clickToClose="true"
			@before-close="closeModal"
			:focusTrap="true"
			:adaptive="true"
			height="auto"
		>
      <v-card>
        <v-card-title class="pa-0 drag-header">
					<div class="d-flex flex-row align-center justify-space-between py-2 px-8 w-100">
						<div class="d-flex flex-row align-center">
							<div>
								<p class="mb-0 font-weight-bold text-primary title">Proyecto</p>
								<p class="mb-0 text-primary caption">Estás creando un <b>{{ this.project.doc_type == 1 ? 'Contrato':'Presupuesto' }}</b> para el proyecto</p>
							</div>
						</div>
						<v-btn class="mx-2" fab dark small color="#FF1209" @click="closeModal">
							<v-icon dark>mdi-close</v-icon>
						</v-btn>
					</div>
				</v-card-title>
        <v-card-text class="pa-0">
          <!-- Loader general -->
          <div v-if="loadingModal" class="d-flex justify-center align-center py-12">
            <v-progress-circular indeterminate color="#524D4D" size="48"></v-progress-circular>
          </div>

          <!-- Paso: documentos existentes -->
          <div v-else-if="step === 'documents'" class="py-4 px-8">
            <div class="mb-4">
              <p class="mb-1 font-weight-bold text-primary">{{ currentDocTypeName }}s existentes</p>
              <p class="caption mb-0" style="color:#606060;">Se encontraron los siguientes documentos asociados a esta línea de proyecto.{{ showNewButton ? ' Puedes abrirlos o crear uno nuevo.' : ' Ya existe un contrato activo.' }}</p>
            </div>
            <v-list dense class="existing-docs-list rounded mb-4">
              <v-list-item
                v-for="doc in existingDocuments"
                :key="doc.id"
                class="existing-doc-item"
                @click="goToDocument(doc)"
              >
                <v-list-item-icon class="mr-3 my-auto">
                  <v-icon color="secondary">mdi-file-document-outline</v-icon>
                </v-list-item-icon>
                <v-list-item-content>
                  <v-list-item-title class="font-weight-medium" style="font-size:13px;">
                    {{ doc.prefix ? doc.prefix.name : currentDocTypeName }}
                  </v-list-item-title>
                  <v-list-item-subtitle style="font-size:11px;">
                    <span class="font-weight-medium">{{ doc.project_code }}</span>
                    <span class="mx-1">·</span>
                    <span>{{ formatDate(doc.created_at) }}</span>
                  </v-list-item-subtitle>
                </v-list-item-content>
                <v-list-item-action class="my-auto">
                  <v-chip
                    x-small
                    :color="getDocStatusColor(doc)"
                    dark
                  >
                    {{ getDocStatusLabel(doc) }}
                  </v-chip>
                </v-list-item-action>
                <v-list-item-action class="my-auto ml-2">
                  <v-icon small color="secondary">mdi-chevron-right</v-icon>
                </v-list-item-action>
              </v-list-item>
            </v-list>
            <div class="d-flex justify-end" v-if="showNewButton">
              <v-btn
                color="#524D4D"
                dark
                raised
                @click="step = 'form'"
              >
                <v-icon left small>mdi-plus</v-icon>
                <p class="mb-0">Crear nuevo {{ currentDocTypeName }}</p>
              </v-btn>
            </div>
						<div v-else>
							<p class="mb-0 font-weight-bold">Sólo puede haber un contrato por proyecto.</p>
							<p class="mb-0">Selecciona el contrato para ir a visualizarlo, podrás trabajarlo y/o crear anexos si fuera necesario.</p>
						</div>
          </div>

          <!-- Paso: formulario -->
          <div v-else-if="step === 'form'" class="py-4 px-8">
            <v-form ref="form" v-model="validform" class="mt-6">
							<v-row>
								<v-col class="py-2">
									<v-select
										v-model="project.client_id"
										:items="$store.state.users.companyClients"
										:loading="$store.state.users.loadingCompanyClients"
										:menu-props="{ top: true, offsetY: true }"
										:disabled="disabledClientProject"
										item-text="profile.name"
										item-value="id"
										label="Seleccione Cliente"
										outlined
										dense
										hide-details
										color="secondary"
										@change="changeClient"
									>
										<template v-slot:item="data">
											<v-list-item-content>
												<v-list-item-title>{{data.item.profile.name}} - <span class="light-blue-text">[{{data.item.email}}]</span></v-list-item-title>
											</v-list-item-content>
										</template>
									</v-select>
									<div class="mt-1">
										<p class="caption mb-0" v-if="clientInfo"><b>Datos del usuario:</b> Email: {{clientInfo.email}} | Teléfono: {{clientInfo.profile.phone}}</p>
										<p class="caption mb-0" v-else>Seleccione usuario para visualizar más datos</p>
									</div>
								</v-col>
							</v-row>

							<v-row>
								<v-col class="py-2">
									<v-select
										v-model="projectName"
										:items="finalClientProjects"
										:loading="loadingClientProjects"
										:menu-props="{ top: true, offsetY: true }"
										:disabled="disabledClientProject"
										item-text="name"
										item-value="uid"
										label="Seleccione Proyecto de Cliente"
										outlined
										dense
										hide-details
										color="secondary"
										@change="changeProject"
									>
										<template v-slot:item="data">
											<v-list-item-content>
												<v-list-item-title><span class="font-weight-bold">[{{getTypeName(data.item.type)}}]</span> {{data.item.name}}</v-list-item-title>
											</v-list-item-content>
										</template>
									</v-select>
								</v-col>
							</v-row>

							<v-row>
								<v-col class="py-2">
									<v-select
										v-model="project.prefix_id"
										:items="filteredPrefixes"
										:loading="loadingClientProjects"
										:menu-props="{ top: true, offsetY: true }"
										item-text="name"
										item-value="id"
										:label="'Seleccione Tipo de ' + (this.project.doc_type == 1 ? 'Contrato':'Presupuesto')"
										outlined
										dense
										hide-details
										color="secondary"
										:rules="requiredRule"
									>
										<template v-slot:item="data">
											<v-list-item-content>
												<v-list-item-title>{{data.item.name}}</v-list-item-title>
											</v-list-item-content>
										</template>
									</v-select>
								</v-col>
							</v-row>

							<v-row>
								<v-col class="py-2">
									<v-autocomplete
										v-model="project.address"
										:items="[...items, ...[project.address]]"
										name="address"
										:search-input.sync="search"
										prepend-inner-icon="mdi-map-marker"
										label="Dirección"
										placeholder="Dirección"
										autocomplete="off"
										outlined
										dense
										hide-details
										:loading="loading"
										:filter="d => d"
										color="secondary"
										item-color="secondary"
										return-object
										:rules="requiredRule"
										@change="change"
									></v-autocomplete>
								</v-col>
							</v-row>

							<v-row>
								<v-col class="py-2">
									<v-select
										v-model="project.state_id"
										:items="$store.state.general.states"
										:loading="$store.state.general.loadingStates"
										:menu-props="{ top: true, offsetY: true }"
										item-text="name"
										item-value="id"
										label="Seleccione Región"
										outlined
										dense
										hide-details
										color="secondary"
										:rules="requiredRule"
										@change="onChangeState"
									></v-select>
								</v-col>
								<v-col class="py-2">
									<v-select
										v-model="project.district_id"
										:items="$store.state.general.cities"
										:loading="$store.state.general.loadingCities"
										item-text="name"
										item-value="id"
										label="Seleccione Municipio"
										outlined
										dense
										color="secondary"
										:rules="requiredRule"
										hide-details
									></v-select>
								</v-col>
							</v-row>

							<v-row>
								<v-col class="py-2">
									<v-select
										v-model="project.zone_id"
										:items="$store.state.projects.zoneIpt"
										:loading="$store.state.projects.loadingZoneIpt"
										:menu-props="{ top: true, offsetY: true }"
										item-text="name"
										item-value="id"
										label="Selecciona Zona Proyecto"
										outlined
										hide-details
										dense
										required
										color="secondary"
										:rules="requiredRule"
									></v-select>
								</v-col>
								<v-col class="py-2">
									<v-combobox
										v-model="projectTypeProxy"
										:items="projectTypeOptions"
										:filter="customFilter"
										:loading="$store.state.companies.loadingCompaniesProjectTypes"
										item-text="name"
										item-value="value"
										dense
										outlined
										label="Selecciona tipo de proyecto"
										clearable
									>
										<template v-slot:item="{ item }">
											<div v-if="item.header" class="font-weight-bold caption">
												{{ item.header }}
											</div>
											<div v-else-if="item.isSubType" class="caption ml-2">
												{{ item.name }}
											</div>
											<div v-else class="caption font-weight-bold">
												{{ item.name }}
											</div>
										</template>
									</v-combobox>
								</v-col>
							</v-row>

              <v-row align="center">
                <v-col cols="12">
                  <div class="d-flex flex-row justify-end">
                    <v-btn
                      class="mx-2 my-1"
                      color="white"
                      raised
                      @click="closeModal"
                    >
                      <p class="mb-0 text-primary">Cancelar</p>
                    </v-btn>
                    <v-btn
                      class="mx-2 my-1"
                      color="#524D4D"
                      raised
                      dark
                      :loading="loading"
                      @click="confirmProject"
                    >
                      <p class="mb-0">Confirmar</p>
                    </v-btn>
                  </div>
                </v-col>
              </v-row>
            </v-form>
          </div><!-- fin v-else formulario -->
        </v-card-text>
      </v-card>
    </modal>
  </div>
</template>

<script>
import { gmapApi } from 'vue2-google-maps'
import {createProject, getUserProjects} from '@/helpers/api/projects'
import { eventBus } from '@/components/Helpers/Events/NotificationsEventBus'
import {getProjectLineDocuments} from '@/helpers/api/projectlines'
import filters from '@/filters/filters'

export default {
  name: 'ProjectModal',
  data: () => ({
		indexModal: + new Date(),
    validform: true,
		search: '',
		items: [],
		autocompleteService: null,
		clientProjects: null,
		loadingClientProjects: false,
		projectName: null,
		budget: null,
		project: {
			name: null,
			client_id: null,
			address: null,
			address_id: null,
			state_id: null,
			district_id: null,
			zone_id: null,
			project_type_id: null,
			licence: false,
			reception: false,
			area: null,
			general_regulations: null,
			specific_regulations: null,
			regulatory_plan: null,
			prefix_id: null,
			preproject_id: null,
			crm_id: null,
			project_id: null,
			from_budget_id: null
		},
		loading: false,
		disabledClientProject: true,
		projectType: null,
		isGeneralStage: true,
		zoneId: null,
		newStageName: null,
		step: 'form',
		existingDocuments: [],
		loadingModal: true,
    requiredRule: [
      v => !!v || 'Campo requerido'
    ]
  }),
  computed: {
		showNewButton(){
			if(this.existingDocuments.length == 0) return true
			return this.project.doc_type != 1
		},
		projectTypeProxy: {
			get() {
				return this.projectType
					? this.projectTypeOptions.find(opt => opt.value === this.projectType)
					: null;
			},
			set(val) {
				this.projectType = val ? val.value : null;
			},
		},
		projectTypeOptions() {
			const options = [];
			if(!this.$store.state.companies.companiesProjectTypesCategories) return options
			this.$store.state.companies.companiesProjectTypesCategories.forEach(category => {
				options.push({ header: category.name });
				category.project_types.forEach(pt => {
					options.push({ name: pt.name, value: pt.id, isParent: true, sub_types: pt.sub_types });
					pt.sub_types.forEach(st => {
						options.push({ name: st.name, value: st.id, isSubType: true });
					});
				});
			});
			return options;
		},
		indexModalIndex(){
			const ordered = [...this.$store.state.general.openModalOrders].sort((a, b) => a - b)
			return ordered.indexOf(this.indexModal) + 10
		},
		google: gmapApi,
		currentDocTypeName(){
			if(this.project.doc_type == 1) return 'Contrato'
			if(this.project.doc_type == 2) return 'Presupuesto'
			return 'Informe'
		},
		clientInfo(){
			const found = this.$store.state.users.companyClients.find(user => user.id == this.project.client_id)
			console.log(found)
			return found
		},
		filteredPrefixes(){
			if(this.project.doc_type == 1){
				return this.$store.state.budget.companiesBudgetPrefixes.filter((prefix) => {
					return prefix.type == 'contract'
				})
			}
			else if(this.project.doc_type == 2){
				return this.$store.state.budget.companiesBudgetPrefixes.filter((prefix) => {
					return prefix.type == 'budget'
				})
			}
			return this.$store.state.budget.companiesBudgetPrefixes.filter((prefix) => {
				return prefix.type == 'report'
			})
		},
		finalClientProjects(){
			if(this.clientProjects){
				return this.clientProjects.map((item) => {
					return {
						...item,
						uid: item.type + '' + item.id
					}
				})
			}
			return null
		}
  },
	watch: {
    search(newSearch) {
      if (newSearch) {
        this.googleSearch(newSearch);
      } else {
        this.items = []; // Limpiar los resultados si no hay búsqueda
      }
    },
	},
  mounted(){
		this.$modal.show('custom_drag_modal')
		this.$store.commit('general/addOpenModalOrders', this.indexModal)
		this.$nextTick(() => {
			this.$gmapApiPromiseLazy().then(() => {
				this.autocompleteService = new this.google.maps.places.AutocompleteService()
			})
			this.$store.dispatch('users/getCompanyClients')
		});
		if(this.$store.state.projects.projectModalInfo && this.$store.state.projects.projectModalInfo.id){
			this.startWithInfo()
		}
		else{
			this.disabledClientProject = false
			this.loadingModal = false
		}
		this.$store.dispatch('projects/getAllZoneIpts')
  },
  methods: {
		customFilter(item, queryText, itemText) {
			if (item.header) return true;
			const text = (itemText || '').toLowerCase();
			const query = queryText.toLowerCase();
			if (text.includes(query)) return true;
			if (item.isParent && item.sub_types?.some(st => st.name.toLowerCase().includes(query))) {
				return true;
			}
			return false;
		},
		getTypeName(type){
			switch (type) {
				case "crm":
					return 'CRM'
				case "preproject":
					return 'PreProyecto'
				case "project":
					return 'Proyecto'
				default:
					break;
			}
			return 'Otro'
		},
		async startWithInfo(){
			this.project = {
				...this.project,
				...{
					client_id: this.$store.state.projects.projectModalInfo.client_id || this.$store.state.projects.projectModalInfo.client?.id,
					doc_type: this.$store.state.projects.projectModalInfo.doc_type,
					zone_id: this.$store.state.projects.projectModalInfo.info.zone_id,
					preproject_id: this.$store.state.projects.projectModalInfo.preproject_id,
					crm_id: this.$store.state.projects.projectModalInfo.info.crm_id,
					from_budget_id: this.$store.state.projects.projectModalInfo.from_budget_id || null,
					project_line_id: this.$store.state.projects.projectModalInfo.project_line_info?.project_line_id || null,
				}
			}

			if(this.project.project_line_id){
				const documents = await getProjectLineDocuments({project_line_id: this.project.project_line_id, show_all_documents: true})
				if(documents.code === 200){
					this.$store.commit('projects/setProjectLineDocuments', documents.documents)
					const docType = this.project.doc_type
					let filtered = []
					if(docType == 1){
						filtered = documents.documents.contracts || []
					} else if(docType == 2){
						filtered = documents.documents.budgets || []
					} else {
						filtered = documents.documents.reports || []
					}
					if(filtered.length > 0){
						this.existingDocuments = filtered
						this.step = 'documents'
					}
				}
			}

			this.budget = this.$store.state.projects.projectModalInfo.budget
			const projects = await getUserProjects({client_id: this.project.client_id})
			if(projects.code == 200){
				this.clientProjects = [
					...projects.project,
					...projects.preproject,
					...projects.crm.map(project => ({
						...project,
						name: project.project_info?.project_detail ?? 'Sin Detalle'
					}))
				]
				this.disabledClientProject = true
				const uid = `${this.$store.state.projects.projectModalInfo.morphable_type}${this.$store.state.projects.projectModalInfo.id}`
				this.projectName = {...this.$store.state.projects.projectModalInfo, ...{uid}}
				this.changeProject(uid)
			}
			this.loadingModal = false
		},
		changeProject(uid){
			let info = null
			if(this.$store.state.projects.projectModalInfo && this.$store.state.projects.projectModalInfo.id){
				info = this.$store.state.projects.projectModalInfo
			}
			else if(this.finalClientProjects && this.finalClientProjects.length > 0){
				info = this.finalClientProjects.find(p => p.uid === uid)
				// info = this.clientProjects.find(client_project => client_project.uid == project)
			}

			this.project = {
				...this.project,
				...{
					crm_id: info.crm_id || null,
					preproject_id: info.preproject_id || null,
					address: null,
					address_id: null,
					state_id: null,
					district_id: null,
					name: info.name || info.info?.project_detail
				}
			}
			const keyMap = {
				crm: 'crm_id',
				preproject: 'preproject_id',
				project: 'project_id'
			}

			const key = keyMap[info.morphable_type]
			if (key) {
				this.project = {
					...this.project,
					[key]: info.id
				}
			}
			if(info && info.info){
				this.project = {
					...this.project,
					...{
						project_type_id: info.info.project_type_id
					}
				}
				this.project.project_type_id = info.info.project_type_id
				if(info.info.address){
					this.project = {
						...this.project,
						...{
							address: info.info.address.address,
							address_id: info.info.address_id,
							state_id: info.info.address.state_id,
							district_id: info.info.address.district_id,
						}
					}
					if(info.info.address.state_id){
						this.onChangeState(this.project.state_id)
					}
				}
			}
		},
		async changeClient(){
			this.clientProjects = null
			this.loadingClientProjects = true
			const projects = await getUserProjects({client_id: this.project.client_id})
			if(projects.code == 200){
				this.clientProjects = projects.preproject
			}
			this.loadingClientProjects = false
		},
		isNumberKey(event) {
			const charCode = event.which ? event.which : event.keyCode;
			if (charCode < 48 || charCode > 57) {
				event.preventDefault();
			}
		},
    googleSearch(query) {
      const options = {
        input: query,
				componentRestrictions: { country: 'CL' }
      };
      this.autocompleteService.getPlacePredictions(options, (predictions, status) => {
        if (status === this.google.maps.places.PlacesServiceStatus.OK) {
          this.items = predictions.map(prediction => ({
            text: prediction.description,
            value: prediction 
          }));
        } else {
          console.error('Error fetching predictions: ', status);
          this.items = [];
        }
      });
    },
		async getPlaceDetails(placeId) {
			if (typeof this.google !== 'undefined' && this.google.maps) {
				const service = new this.google.maps.places.PlacesService(document.createElement('div'));
				
				service.getDetails({ placeId }, (place, status) => {
					if (status === this.google.maps.places.PlacesServiceStatus.OK) {
						const { geometry } = place;
						this.newSchedule.coordinates = {
							lat: geometry.location.lat(),
							lng: geometry.location.lng()
						}
					}
				});
			} else {
				console.error('Google Maps API no está cargada');
			}
		},
    change(value) {
			this.placeChanged = true
			this.getPlaceDetails(value.value.place_id)
      this.project.address = value.text;
			this.project.address_id = null
    },
		onChangeState(state){
			this.$store.dispatch('general/getCities', state)
		},
		formatDate(dateStr){
			if(!dateStr) return ''
			const d = new Date(dateStr)
			return d.toLocaleDateString('es-CL', { day: '2-digit', month: '2-digit', year: 'numeric' })
		},
		getDocStatusLabel(doc){
			const status = doc.last_status?.status
			const docType = this.project.doc_type
			if(docType == 1) return filters.contractStatus(status)
			if(docType == 2) return filters.budgetStatus(status)
			return filters.budgetStatus(status)
		},
		getDocStatusColor(doc){
			const status = doc.last_status?.status
			const docType = this.project.doc_type
			if(docType == 1) return filters.contractStatusColor(status)
			if(docType == 2) return filters.budgetStatusColor(status)
			return filters.budgetStatusColor(status)
		},
		goToDocument(doc){
			const docType = this.project.doc_type
			if(docType == 1){
				this.$router.push({ name: 'ContractDetail', query: { id: doc.id } })
			} else if(docType == 2){
				this.$router.push({ name: 'BudgetDetail', query: { id: doc.id } })
			} else {
				this.$router.push({ name: 'ReportDetail', query: { id: doc.id } })
			}
			this.closeModal()
		},
    closeModal(){
			this.$store.commit('projects/setProjectModalInfo', null)
			this.$store.commit('projects/setProjectModal', false)
			this.projectType = null
			this.zoneId = null
			this.step = 'form'
			this.existingDocuments = []
			this.loadingModal = true
      this.validform = true
      this.loading = false
			this.project = {
				name: null,
				address: null,
				client_id: null,
				state_id: null,
				district_id: null,
				zone_id: null,
				project_type_id: null,
				licence: false,
				reception: false,
				area: null,
				general_regulations: null,
				specific_regulations: null,
				regulatory_plan: null
			}
    },
		closeAllPossibleCreatorsModals(){
			this.$store.commit('reports/setReportComponentModalId', {close_all: true})
			this.$store.commit('budget/setBudgetComponentModalId', null)

			eventBus.$emit('reloadInformation', {})
		},
		async confirmProject(){
      if(this.$refs.form.validate()){
				this.loading = true
				this.project.project_type_id = this.projectType
				let data = this.project
				const contract = await createProject(data)
				if(contract.code == 200){
					this.$store.dispatch('projects/getProjects')
					this.$store.commit('general/setNewCreatedElement', {type: 'project', value: contract.project})
					this.closeModal()
					this.closeAllPossibleCreatorsModals()
					if(contract.existent){
						const source = contract.contract || contract.budget;
						if (source) {
							const dataInfo = {
								...source,
								doc_type: data.doc_type,
							};
							this.$store.commit('general/setExistentDocumentNotificationModalData', dataInfo);
							this.$store.commit('general/setExistentDocumentNotificationModal', true);
						}
						return
					}
          const messageData = {
            message: 'Se ha creado correctamente el proyecto',
            title: 'Proyecto Creado',
            type: 'success',
            icon: 'mdi-check-circle',
            color: '#3EBFA3',
          }
          this.setMessage(messageData)
					if(contract.contract){
						this.$router.push({ name: 'ContractDetail', query: { id: contract.contract.id } })
					}
					else if(contract.budget){
						localStorage.setItem('last_working_budget', JSON.stringify(contract.budget))
						this.$router.push({ name: 'BudgetDetail', query: { id: contract.budget.id } })
					}
        }
        else{
          const messageData = {
            message: 'Hubo un error en la operación, por favor intenta nuevamente',
            title: 'Ha ocurrido un error',
						type: 'danger',
						icon: 'mdi-close-octagon',
						color: '#FF1209',
          }
          this.setMessage(messageData)
        }
				this.loading = false
			}
		},
    setMessage(messageData){
      const data = {
        message: messageData.message,
        title: messageData.title,
        created: new Date(),
        type: messageData.type,
        icon: messageData.icon,
        color: messageData.color,
      }
      this.$store.dispatch('general/addMessage', data)
    },
  }
};
</script>

<style scoped>
	.profile-avatar{
		cursor: pointer;
		position: relative;
		width: 200px;
	}
	.profile-image{
		width: 100%;
		height: 150px;
	}
	.logo-text{
		position: absolute;
		bottom: 0;
		background-color: rgba(0, 0, 0, 0.7);
		width: 100%;
		color: white;
		font-size: 12px;
		padding: 5px 10px;
	}
  .info-container{
    color: #606060;
  }
  .text-primary{
    color: #524D4D;
  }
  .info-icon{
    background-color: #524D4D;
    border-radius: 25px;
    padding: 3px;
    width: 35px;
    height: 35px;
  }
  .info-icon.small-icon{
    width: 25px;
    height: 25px;
  }
  .info-img{
    width: 100%;
    max-height: 50px;
    object-fit: contain;
  }
  .adjuntos-container{
    background-color: #EFEFEF;
    max-height: 95px;
    overflow-y: scroll;
  }
  .interaction-container{
    width: 100%;
    background-color: #E5E9F2;
    border-radius: 5px;
    position: relative;
    padding: 12px;
  }
  .user-avatar.small{
    width: 25px;
    height: 25px;
  }
  .light-blue-text{color: #959595;}
  .interaction-container .icon-arrow{
    font-size: 80px;
    position: absolute;
    top: 20px;
    line-height: inherit;
  }
  .interaction-container.right .icon-arrow{
    left: -46px;
  }
  .interaction-container.left .icon-arrow{
    right: -46px;
  }
  /deep/ .custom-input-numbers .v-input__slot{padding:0!important;}
  /deep/ .custom-input-numbers .v-input__prepend-inner,
  /deep/ .custom-input-numbers .v-input__append-inner,
  /deep/ .custom-text-input .v-input__append-inner{margin-top:0!important;}
  /deep/ .custom-input-numbers input {text-align: center }
  .existing-docs-list{
    background-color: #F5F5F5;
    border: 1px solid #E0E0E0;
  }
  .existing-doc-item{
    border-bottom: 1px solid #E8E8E8;
    cursor: pointer;
    transition: background-color .15s;
  }
  .existing-doc-item:last-child{
    border-bottom: none;
  }
  .existing-doc-item:hover{
    background-color: #EBEBEB;
  }
</style>